import Page from 'components/new/Page';
import { useLayoutContext } from 'contexts/LayoutContext';

import { useParams } from 'react-router-dom';
import { COMMITMENTS } from 'adminConstants/routes';
import Avatar from 'further-ui/components/Avatar';
import Divider from '@mui/material/Divider';
import { useEffect } from 'react';
import { match } from 'ts-pattern';
import { Box, Skeleton, styled } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import ScrollStepper from './ScrollStepper';
import {
  useCommitmentViewSections,
  Section,
} from './useCommitmentViewSections';
import CustomHeaderActions from './CustomHeader';
import { useCommitmentView } from './useCommitmentView';
import Overview from './Sections/Overview';
import FundPerformance from './Sections/FundPerformance';
import LiveHoldingsByClass from './Sections/Holdings/LiveHoldingsByClass';
import RealisedHoldingsByClass from './Sections/Holdings/RealisedHoldingsByClass';
import SectionHeader from './SectionHeader';
import {
  HoldingsType,
  useHoldingsSortState,
} from './Sections/Holdings/useHoldingsSortState';
import Toggle from 'components/new/Toggle';
import FeesAndCharges from './Sections/FeesAndCharges';
import PreferredReturnBalance from './Sections/PreferredReturnBalance';
import FundCloseHistory from './Sections/FundCloseHistory';
import CapitalBalanceAndReconciledPayments from './Sections/CapitalBalanceAndReconciledPayments';
import IncomeAndExpenses from './Sections/IncomeAndExpenses';
import CapitalHistory from './Sections/CapitalActions';
import Pcap from './Sections/Pcap';
import { HoldingsSortBy } from './types';
import LiveHoldingsByHolding from './Sections/Holdings/LiveHoldingsByHolding';
import RealisedHoldingsByHolding from './Sections/Holdings/RealisedHoldingsByHolding';
import { useSelectedCommitment } from './useSelectedCommitment';
import MultipleCommitmentTabs from './MultipleCommitmentTabs';
import { CalendarDay } from 'further-ui/utils';

const ScrollSection = styled('div')(() => ({
  scrollMarginTop: '88px',
}));

const StickyBox = styled(Box)(() => ({
  position: 'sticky',
  top: 160,
}));

const ContentContainer = styled(Column)({ flex: 1, minWidth: 0 });
const date = new CalendarDay(new Date());

const ViewCommitment: React.FC = () => {
  const { investorId } = useParams<{ investorId: string }>();
  const { selectedVehicleId, setBreadcrumbs, breadcrumbs } = useLayoutContext();
  const { sections, toggleSection } = useCommitmentViewSections();
  const { commitmentId, setCommitmentId } = useSelectedCommitment();

  const {
    data,
    updateCommitment,
    isLoading,
    calculatingCurrencyConversions,
    commitmentIds,
    investorName,
  } = useCommitmentView({
    commitmentId,
    vehicleId: selectedVehicleId ?? '',
    investorId,
    date,
  });

  const { setSortState, state: holdingsSortState } = useHoldingsSortState();

  const headingText = investorName ?? '';
  const hasMultipleCommitments = commitmentIds.length > 1;

  useEffect(() => {
    if (breadcrumbs[breadcrumbs.length - 1] === headingText) return;
    setBreadcrumbs([{ label: 'Commitments', link: COMMITMENTS }, headingText]);
  }, [headingText, breadcrumbs, setBreadcrumbs]);

  return (
    <Page
      tabTitle="Commitments"
      goBackAction={COMMITMENTS}
      heading={
        <Row spacing="sm" centered>
          {isLoading && !investorName ? (
            <>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={120} />
            </>
          ) : (
            <>
              <Avatar color="random" text={headingText} size="lg" />
              {headingText}
            </>
          )}
        </Row>
      }
      withHeadingDivider
      withStickyHeader
      customActions={
        <CustomHeaderActions
          vehicleId={data?.vehicleId}
          commitmentIds={commitmentIds}
          hasDocuments={data?.hasDocuments ?? false}
          closeDate={data?.closeDate}
          commitmentDate={data?.commitmentDate}
          isLoading={isLoading}
        />
      }
      spacing="xl"
    >
      <Row fullWidth spacing="lg">
        <ContentContainer spacing="xl" fullWidth>
          {hasMultipleCommitments ? (
            <MultipleCommitmentTabs
              onChange={setCommitmentId}
              commitmentIds={commitmentIds}
              selectedCommitmentId={commitmentId}
            />
          ) : null}
          {isLoading ? (
            <Column spacing="xl">
              {Array.from({ length: 3 }).map((_, index) => (
                <Column key={index}>
                  <Skeleton variant="text" width={300} />
                  <Skeleton height={30} />
                  <Skeleton height={75} />
                  <Skeleton height={50} />
                </Column>
              ))}
            </Column>
          ) : (
            sections.map((section, index) => {
              const showInvestorConvertedCurrency =
                data.currencySettings.investorCurrency !==
                data.currencySettings.currencyLcy;
              if (section.id === Section.FundCloseHistory && commitmentId)
                return null;
              return (
                <ScrollSection id={section.id} key={section.id}>
                  <Column spacing="xl">
                    {index !== 0 ? <Divider /> : null}
                    <Column spacing="sm" fullWidth>
                      <SectionHeader
                        isExpanded={section.isExpanded}
                        onToggleExpanded={() => toggleSection(section.id)}
                        title={section.customTitle ?? section.label}
                        customActions={match(section)
                          .with({ isExpanded: false }, () => null)
                          .with(
                            { id: Section.LiveHoldings },
                            { id: Section.RealisedHoldings },
                            ({ id }) => {
                              const key =
                                id === Section.LiveHoldings
                                  ? HoldingsType.Live
                                  : HoldingsType.Realised;
                              return (
                                <Row spacing="xs">
                                  <Toggle
                                    value={holdingsSortState[key]}
                                    onChange={(value) => {
                                      if (!value) return;
                                      setSortState({ [key]: value });
                                    }}
                                    options={[
                                      {
                                        value: HoldingsSortBy.ByHolding,
                                        label: 'Holding',
                                      },
                                      {
                                        value: HoldingsSortBy.ByClass,
                                        label: 'Class',
                                      },
                                    ]}
                                  />
                                </Row>
                              );
                            },
                          )
                          .otherwise(() => null)}
                      />
                      {match(section)
                        .with({ isExpanded: false }, () => null)
                        .with({ id: Section.CommitmentOverview }, () => (
                          <Overview
                            vehicleId={data.vehicleId}
                            currencySettings={data.currencySettings}
                            overview={data.overview}
                            investorName={data.investorName}
                            onChange={updateCommitment}
                            isCommitmentAggregate={
                              hasMultipleCommitments && !commitmentId
                            }
                            showInvestorConvertedCurrency={
                              showInvestorConvertedCurrency
                            }
                            calculatingCurrencyConversions={
                              calculatingCurrencyConversions
                            }
                          />
                        ))
                        .with({ id: Section.FundPerformance }, () => (
                          <FundPerformance
                            currencySettings={data.currencySettings}
                            fundPerformance={data.fundPerformance}
                            calculatingCurrencyConversions={
                              calculatingCurrencyConversions
                            }
                            showInvestorConvertedCurrency={
                              showInvestorConvertedCurrency
                            }
                          />
                        ))
                        .with({ id: Section.LiveHoldings }, () =>
                          holdingsSortState[HoldingsType.Live] ===
                          HoldingsSortBy.ByClass ? (
                            <LiveHoldingsByClass
                              currencySettings={data.currencySettings}
                              holdingsByClass={data.liveHoldings.byClass}
                            />
                          ) : (
                            <LiveHoldingsByHolding
                              currencySettings={data.currencySettings}
                              holdingsByHolding={data.liveHoldings.byHolding}
                            />
                          ),
                        )
                        .with({ id: Section.RealisedHoldings }, () =>
                          holdingsSortState[HoldingsType.Realised] ===
                          HoldingsSortBy.ByClass ? (
                            <RealisedHoldingsByClass
                              currencySettings={data.currencySettings}
                              holdingsByClass={data.realisedHoldings.byClass}
                            />
                          ) : (
                            <RealisedHoldingsByHolding
                              currencySettings={data.currencySettings}
                              holdingsByHolding={
                                data.realisedHoldings.byHolding
                              }
                            />
                          ),
                        )
                        .with({ id: Section.FeesAndCharges }, () => (
                          <FeesAndCharges
                            currencySettings={data.currencySettings}
                            futureFeesAndCharges={data.futureFeesAndCharges}
                            feesAndCharges={data.feesAndCharges}
                          />
                        ))
                        .with({ id: Section.PreferredReturnBalance }, () => (
                          <PreferredReturnBalance
                            commitmentIds={[data.id]}
                            vehicleId={data.vehicleId}
                            currencySettings={data.currencySettings}
                            preferredReturnBalance={data.preferredReturnBalance}
                            date={date}
                          />
                        ))
                        .with({ id: Section.FundCloseHistory }, () => (
                          <FundCloseHistory
                            currencySettings={data.currencySettings}
                            closeHistory={data.closeHistory}
                          />
                        ))
                        .with(
                          { id: Section.CapitalBalanceAndReconciledPayments },
                          () => (
                            <CapitalBalanceAndReconciledPayments
                              currencySettings={data.currencySettings}
                              capitalBalanceAndReconciledPayments={
                                data.capitalBalanceAndReconciledPayments
                              }
                            />
                          ),
                        )
                        .with({ id: Section.IncomesAndExpenses }, () => (
                          <IncomeAndExpenses
                            currencySettings={data.currencySettings}
                            incomeAndExpenses={data.incomeAndExpenses}
                          />
                        ))
                        .with({ id: Section.CapitalHistory }, () => (
                          <CapitalHistory
                            capitalActions={data.capitalActions}
                            currencySettings={data.currencySettings}
                          />
                        ))
                        .with({ id: Section.Pcap }, () => (
                          <Pcap
                            investorId={investorId}
                            commitmentId={commitmentId ?? undefined}
                            vehicleId={data.vehicleId}
                            currencySettings={data.currencySettings}
                            investorName={data.investorName}
                          />
                        ))
                        .otherwise(() => null)}
                    </Column>
                  </Column>
                </ScrollSection>
              );
            })
          )}
        </ContentContainer>
        <Box sx={{ flexShrink: 0 }}>
          <StickyBox>
            <ScrollStepper
              intersectionRootMargin="-120px 0px 0px 0px"
              sections={sections}
            />
          </StickyBox>
        </Box>
      </Row>
    </Page>
  );
};

export default ViewCommitment;
